import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
} from "@mui/material";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import NivelService from "../../../services/LevelService";
import GradoService from "../../../services/GradesService";

// ======================================================
// CACHÉ PARA EVITAR PETICIONES REDUNDANTES
// ======================================================
const cache = {
  cursosPorNivel: {},
  gradosPorNivel: {},
};

const LS_KEY = "seleccionPaso1";

const pageSx = {
  pb: 0.5,
};

const paperSx = {
  p: { xs: 3, md: 5 },
  borderRadius: "20px",
  border: "1px solid #e2e8f0",
  boxShadow: "0 10px 40px rgba(15, 23, 42, 0.04)",
  backgroundColor: "#ffffff",
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#cbd5e1",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#00c7db",
      borderWidth: 1.5,
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e2e8f0",
  },
  "& .MuiInputLabel-root": {
    color: "#64748b",
    fontWeight: 500,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#00c7db",
  },
};

const placeholderSx = {
  color: "#94a3b8",
};

const Paso1 = ({ onNext, setSelectores }) => {
  // BACKEND DATA
  const [niveles, setNiveles] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [gradosFiltrados, setGradosFiltrados] = useState([]);

  // SELECCIONES
  const [nivelSeleccionado, setNivelSeleccionado] = useState("");
  const [cursoSeleccionado, setCursoSeleccionado] = useState("");
  const [gradoSeleccionado, setGradoSeleccionado] = useState("");

  // LOADING STATES
  const [loading, setLoading] = useState(true);
  const [loadingCursos, setLoadingCursos] = useState(false);
  const [loadingGrados, setLoadingGrados] = useState(false);

  // ======================================================
  // CARGA INICIAL DE NIVELES
  // ======================================================
  useEffect(() => {
    NivelService.listarNiveles()
      .then((res) => setNiveles(res.data))
      .finally(() => setLoading(false));
  }, []);

  // ======================================================
  // GUARDAR EN LOCALSTORAGE AUTOMÁTICAMENTE
  // ======================================================
  useEffect(() => {
    if (!nivelSeleccionado) return;

    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        nivel: nivelSeleccionado,
        curso: cursoSeleccionado,
        grado: gradoSeleccionado,
      })
    );
  }, [nivelSeleccionado, cursoSeleccionado, gradoSeleccionado]);

  // ======================================================
  // MÉTODOS DE CACHÉ
  // ======================================================
  const getCursosPorNivel = useCallback(async (nivelId) => {
    if (cache.cursosPorNivel[nivelId]) return cache.cursosPorNivel[nivelId];
    const res = await GradoService.listarAreasPorNivel(nivelId);
    cache.cursosPorNivel[nivelId] = res.data;
    return res.data;
  }, []);

  const getGradosPorNivel = useCallback(async (nivelId) => {
    if (cache.gradosPorNivel[nivelId]) return cache.gradosPorNivel[nivelId];
    const res = await NivelService.listarGradosPorNivel(nivelId);
    cache.gradosPorNivel[nivelId] = res.data;
    return res.data;
  }, []);

  // ======================================================
  // RESTORE DESDE LOCALSTORAGE
  // ======================================================
  const restore = useCallback(async ({ nivel, curso, grado }) => {
    try {
      if (nivel) {
        const nivelId = Number(nivel);
        setNivelSeleccionado(nivelId);

        const cursosNivel = await getCursosPorNivel(nivelId);
        setCursos(cursosNivel);

        if (curso) {
          const cursoId = Number(curso);
          setCursoSeleccionado(cursoId);

          const grados = await getGradosPorNivel(nivelId);
          setGradosFiltrados(grados);

          if (grado) {
            setGradoSeleccionado(Number(grado));
          }
        }
      }
    } catch (err) {
      console.error("Error restaurando localStorage:", err);
    }
  }, [getCursosPorNivel, getGradosPorNivel]);

  // ======================================================
  // RESTAURAR LOCALSTORAGE CUANDO NIVELES ESTÉN LISTOS
  // ======================================================
  useEffect(() => {
    if (niveles.length === 0) return;

    const guardado = localStorage.getItem(LS_KEY);
    if (!guardado) return;

    try {
      const data = JSON.parse(guardado);
      restore(data);
    } catch {
      localStorage.removeItem(LS_KEY);
    }
  }, [niveles, restore]);

  // ======================================================
  // CAMBIO DE NIVEL
  // ======================================================
  const handleNivelChange = async (e) => {
    const nivelId = Number(e.target.value);
    setNivelSeleccionado(nivelId);

    setCursos([]);
    setCursoSeleccionado("");
    setGradosFiltrados([]);
    setGradoSeleccionado("");

    setLoadingCursos(true);
    try {
      const cursosNivel = await getCursosPorNivel(nivelId);
      setCursos(cursosNivel);
    } finally {
      setLoadingCursos(false);
    }
  };

  // ======================================================
  // CAMBIO DE CURSO
  // ======================================================
  const handleCursoChange = async (e) => {
    const cursoId = Number(e.target.value);
    setCursoSeleccionado(cursoId);

    setGradosFiltrados([]);
    setGradoSeleccionado("");

    setLoadingGrados(true);
    try {
      const grados = await getGradosPorNivel(nivelSeleccionado);
      setGradosFiltrados(grados);
    } finally {
      setLoadingGrados(false);
    }
  };

  // ======================================================
  // CAMBIO DE GRADO
  // ======================================================
  const handleGradoChange = (e) => {
    setGradoSeleccionado(Number(e.target.value));
  };

  // ======================================================
  // CONTINUAR
  // ======================================================
  const handleContinuar = () => {
    setSelectores({
      nivel_id: nivelSeleccionado,
      nivel_nombre: niveles.find((n) => n.id === nivelSeleccionado)?.nombre,

      area_id: cursoSeleccionado,
      area_nombre: cursos.find((c) => c.id === cursoSeleccionado)?.nombre,

      grado_id: gradoSeleccionado,
      grado_nombre: gradosFiltrados.find((g) => g.id === gradoSeleccionado)
        ?.descripcion,
    });

    onNext();
  };

  // ======================================================
  // RENDER
  // ======================================================
  if (loading) {
    return (
      <Box sx={pageSx}>
        <Box display="flex" justifyContent="center" mt={10}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={pageSx}>
      <Box sx={{ mb: 2, textAlign: "initial" }}>
        <Typography variant="h4" sx={{ color: "#8f8f8f", letterSpacing: "-0.02em", mb: 1 }}>
          Recopilación de Datos
        </Typography>
      </Box>

      <Paper elevation={0} sx={paperSx}>
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <MenuBookRoundedIcon sx={{ color: "#00c7db" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", fontSize: "1.05rem" }}>
              Diseño Curricular
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Nivel Educativo"
                value={nivelSeleccionado}
                onChange={handleNivelChange}
                sx={textFieldSx}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => {
                    if (value === "") {
                      return <Box component="span" sx={placeholderSx}>Selecciona un nivel</Box>;
                    }
                    return niveles.find((item) => item.id === value)?.nombre ?? "";
                  },
                }}
              >
                <MenuItem disabled value="">
                  Selecciona un nivel
                </MenuItem>
                {niveles.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Área Temática"
                value={cursoSeleccionado}
                onChange={handleCursoChange}
                disabled={!nivelSeleccionado || loadingCursos}
                sx={textFieldSx}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: loadingCursos ? (
                    <InputAdornment position="start">
                      <CircularProgress size={20} sx={{ color: "#00c7db" }} />
                    </InputAdornment>
                  ) : null,
                }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => {
                    if (value === "") {
                      return <Box component="span" sx={placeholderSx}>Selecciona un área temática</Box>;
                    }
                    return cursos.find((item) => item.id === value)?.nombre ?? "";
                  },
                }}
              >
                <MenuItem disabled value="">
                  Selecciona un área temática
                </MenuItem>
                {cursos.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Grado Escolar"
                value={gradoSeleccionado}
                onChange={handleGradoChange}
                disabled={!cursoSeleccionado || loadingGrados}
                sx={textFieldSx}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: loadingGrados ? (
                    <InputAdornment position="start">
                      <CircularProgress size={20} sx={{ color: "#00c7db" }} />
                    </InputAdornment>
                  ) : null,
                }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => {
                    if (value === "") {
                      return <Box component="span" sx={placeholderSx}>Selecciona un grado</Box>;
                    }
                    return gradosFiltrados.find((item) => item.id === value)?.descripcion ?? "";
                  },
                }}
              >
                <MenuItem disabled value="">
                  Selecciona un grado
                </MenuItem>
                {gradosFiltrados.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.descripcion}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 1.5 }}>
        <Button
          variant="contained"
          endIcon={<ChevronRightRoundedIcon />}
          onClick={handleContinuar}
          disabled={!nivelSeleccionado || !cursoSeleccionado || !gradoSeleccionado}
          sx={{
            borderRadius: "10px",
            py: 1,
            px: 4,
            fontWeight: 700,
            "&.Mui-disabled": {
              backgroundColor: "#cbd5e1",
              color: "#ffffff",
            },
          }}
        >
          Continuar
        </Button>
      </Box>
    </Box>
  );
};

export default Paso1;
